import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password !== confirm) {
            setError("Passwords do not match.");
            return;
        }
        setBusy(true);
        try {
            const res = await fetch(`${API}/api/auth/reset-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token, password }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not reset password.");
            setDone(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#0B1120] px-6 font-adminSans text-white">
            <div className="w-full max-w-md rounded-lg border border-white/10 bg-white p-8 text-[#0F172A] shadow-2xl" data-testid="admin-reset-password-page">
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-[#0B1120] font-adminDisplay text-xs font-bold tracking-[0.12em] text-[#D4AF37]">AGL</span>
                <h1 className="mt-6 font-adminDisplay text-3xl font-bold tracking-tight">Choose New Password</h1>
                {!token && <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">This reset link is missing its token.</p>}
                {done ? (
                    <div data-testid="admin-reset-success" className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-700">
                        Password updated successfully. You can now sign in with the new password.
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-6 space-y-5" data-testid="admin-reset-form">
                        <div>
                            <label htmlFor="admin-new-password" className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]">New password</label>
                            <input id="admin-new-password" data-testid="admin-new-password-input" type="password" required minLength={12} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-md border border-[#E2E8F0] px-4 py-3 text-sm outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30" autoComplete="new-password" />
                        </div>
                        <div>
                            <label htmlFor="admin-confirm-password" className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]">Confirm password</label>
                            <input id="admin-confirm-password" data-testid="admin-confirm-password-input" type="password" required minLength={12} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full rounded-md border border-[#E2E8F0] px-4 py-3 text-sm outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30" autoComplete="new-password" />
                        </div>
                        <p className="text-xs text-[#64748B]">Use at least 12 characters with letters and numbers.</p>
                        {error && <p data-testid="admin-reset-error" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
                        <button type="submit" data-testid="admin-reset-submit" disabled={busy || !token} className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#0B1120] px-5 py-3.5 text-xs font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#1E293B] disabled:opacity-60">
                            {busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
                            Update Password
                        </button>
                    </form>
                )}
                <Link to="/admin/login" data-testid="reset-back-login" className="mt-6 inline-block text-xs font-bold uppercase tracking-[0.16em] text-[#64748B] hover:text-[#D4AF37]">← Back to sign in</Link>
            </div>
        </div>
    );
}
